import { FC, useState, useEffect } from 'react'
import { jingqueAdminApi, ChatTemplate, CreateChatTemplateRequest, LLMConnector } from '@/api/jingqueAdminApi'

export const ChatTemplatesPage: FC = () => {
  const [templates, setTemplates] = useState<ChatTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [connectors, setConnectors] = useState<LLMConnector[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<ChatTemplate | null>(null)
  const [saving, setSaving] = useState(false)
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  const loadTemplates = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await jingqueAdminApi.chatTemplates.list(page, pageSize)
      setTemplates(res.items)
      setTotal(res.total)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const loadConnectors = async () => {
    try {
      const res = await jingqueAdminApi.connectors.list()
      setConnectors(res.connectors || [])
    } catch {}
  }

  useEffect(() => { loadTemplates() }, [page])
  useEffect(() => { loadConnectors() }, [])

  const handleCreate = async (data: CreateChatTemplateRequest) => {
    setSaving(true)
    try {
      await jingqueAdminApi.chatTemplates.create(data)
      setShowCreate(false)
      loadTemplates()
    } catch (e: any) {
      alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string, data: any) => {
    setSaving(true)
    try {
      await jingqueAdminApi.chatTemplates.update(id, data)
      setEditing(null)
      loadTemplates()
    } catch (e: any) {
      alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDragStart = (index: number) => setDragIndex(index)

  const handleDrop = async (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) return
    const updated = [...templates]
    const [moved] = updated.splice(dragIndex, 1)
    updated.splice(targetIndex, 0, moved)
    setTemplates(updated)
    try {
      await jingqueAdminApi.chatTemplates.batchSortOrder(
        updated.map((t, i) => ({ id: t.id, sortOrder: i }))
      )
    } catch (e: any) {
      alert(e.message)
    }
    setDragIndex(null)
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-800">聊天模板</h1>
        <div className="flex gap-2">
          <button onClick={loadTemplates} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">刷新</button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg">新建模板</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left w-10"></th>
                  <th className="px-4 py-3 text-left">名称</th>
                  <th className="px-4 py-3 text-left">状态</th>
                  <th className="px-4 py-3 text-left">Connector</th>
                  <th className="px-4 py-3 text-left">超时(s)</th>
                  <th className="px-4 py-3 text-left">Agent轮数</th>
                  <th className="px-4 py-3 text-left">更新时间</th>
                  <th className="px-4 py-3 text-left w-24">操作</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t, i) => (
                  <tr
                    key={t.id}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(i)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-gray-400">☰</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{t.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{t.connectorId || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{t.timeoutSeconds}</td>
                    <td className="px-4 py-3 text-gray-600">{t.maxAgentRounds}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(t.updatedAt)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setEditing(t)} className="text-primary hover:underline text-sm">编辑</button>
                    </td>
                  </tr>
                ))}
                {templates.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">暂无数据</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {total > pageSize && (
            <div className="flex justify-center gap-2 mt-6">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">上一页</button>
              <span className="px-3 py-1 text-sm text-gray-600">{page} / {Math.ceil(total / pageSize)}</span>
              <button disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">下一页</button>
            </div>
          )}
        </>
      )}

      {showCreate && (
        <TemplateModal
          connectors={connectors}
          saving={saving}
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editing && (
        <TemplateModal
          template={editing}
          connectors={connectors}
          saving={saving}
          onSave={(data) => handleUpdate(editing.id, data)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

interface TemplateModalProps {
  template?: ChatTemplate
  connectors: LLMConnector[]
  saving: boolean
  onSave: (data: CreateChatTemplateRequest) => void
  onClose: () => void
}

const TemplateModal: FC<TemplateModalProps> = ({ template, connectors, saving, onSave, onClose }) => {
  const [form, setForm] = useState({
    templateId: template?.templateId || '',
    name: template?.name || '',
    description: template?.description || '',
    systemPrompt: template?.systemPrompt || '',
    userPrompt: template?.userPrompt || '',
    requiredVariables: (template?.requiredVariables || []).join(', '),
    connectorId: template?.connectorId || '',
    timeoutSeconds: template?.timeoutSeconds || 60,
    tools: (template?.tools || []).join(', '),
    maxAgentRounds: template?.maxAgentRounds || 0,
  })

  const handleSubmit = () => {
    onSave({
      templateId: form.templateId,
      name: form.name,
      description: form.description || undefined,
      systemPrompt: form.systemPrompt || undefined,
      userPrompt: form.userPrompt,
      requiredVariables: form.requiredVariables ? form.requiredVariables.split(',').map(s => s.trim()).filter(Boolean) : [],
      connectorId: form.connectorId || undefined,
      timeoutSeconds: form.timeoutSeconds || undefined,
      tools: form.tools ? form.tools.split(',').map(s => s.trim()).filter(Boolean) : [],
      maxAgentRounds: form.maxAgentRounds || undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">{template ? '编辑模板' : '新建模板'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">模板ID {template ? '(不可修改)' : ''}</label>
            <input
              value={form.templateId}
              onChange={e => setForm(f => ({ ...f, templateId: e.target.value }))}
              disabled={!!template}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
              placeholder="如: persona_profile_v1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">名称</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
            <textarea value={form.systemPrompt} onChange={e => setForm(f => ({ ...f, systemPrompt: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" rows={4} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Prompt</label>
            <textarea value={form.userPrompt} onChange={e => setForm(f => ({ ...f, userPrompt: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" rows={4} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Connector</label>
              <select value={form.connectorId} onChange={e => setForm(f => ({ ...f, connectorId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">请选择</option>
                {connectors.map(c => <option key={c.id} value={c.connectorId}>{c.connectorName} - {c.modelName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">超时(秒)</label>
              <input type="number" value={form.timeoutSeconds} onChange={e => setForm(f => ({ ...f, timeoutSeconds: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">必填变量(逗号分隔)</label>
              <input value={form.requiredVariables} onChange={e => setForm(f => ({ ...f, requiredVariables: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="name, age" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent最大轮数</label>
              <input type="number" value={form.maxAgentRounds} onChange={e => setForm(f => ({ ...f, maxAgentRounds: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">工具(逗号分隔)</label>
            <input value={form.tools} onChange={e => setForm(f => ({ ...f, tools: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="get_weather, search" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">取消</button>
          <button onClick={handleSubmit} disabled={saving || !form.templateId || !form.name || !form.userPrompt} className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50">
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDate(s: string) {
  if (!s) return '-'
  return new Date(s).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })
}
